/**
 * GARDEN-130: Client-side Error Tracking & Performance Monitoring
 * Captures JS errors, unhandled rejections, and performance metrics
 */

const API_BASE_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || window.location.origin;

interface ErrorContext {
  url: string;
  userAgent: string;
  timestamp: number;
  userId?: string;
  guildId?: string;
  metadata?: Record<string, unknown>;
}

interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 'bytes' | 'count';
  timestamp: number;
  metadata?: Record<string, unknown>;
}

class ErrorTracker {
  private static instance: ErrorTracker;
  private errorQueue: Array<{ message: string; stack?: string; context: ErrorContext }> = [];
  private perfQueue: PerformanceMetric[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private readonly MAX_QUEUE_SIZE = 50;
  private currentGuildId: string | null = null;
  private currentUserId: string | null = null;
  private isEnabled = true;

  // GARDEN-210: Store observers and handlers for proper cleanup
  private perfObservers: PerformanceObserver[] = [];
  private boundErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private boundRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
  private boundVisibilityHandler: (() => void) | null = null;
  private boundLoadHandler: (() => void) | null = null;
  private boundBeforeUnloadHandler: (() => void) | null = null;
  private originalConsoleError: typeof console.error | null = null;

  private constructor() {
    this.setupErrorHandlers();
    this.setupPerformanceObserver();
    this.startFlushTimer();
  }

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  /**
   * Set current context for error reporting
   */
  setContext(guildId?: string, userId?: string): void {
    if (guildId) this.currentGuildId = guildId;
    if (userId) this.currentUserId = userId;
  }

  /**
   * Disable error tracking (e.g., for privacy compliance)
   */
  disable(): void {
    this.isEnabled = false;
  }

  /**
   * Enable error tracking
   */
  enable(): void {
    this.isEnabled = true;
  }

  /**
   * Capture an error manually
   */
  captureError(error: Error, metadata?: Record<string, unknown>): void {
    if (!this.isEnabled) return;

    this.errorQueue.push({
      message: error.message,
      stack: error.stack,
      context: this.buildContext(metadata),
    });

    this.maybeFlush();
  }

  /**
   * Capture a warning message
   */
  captureWarning(message: string, metadata?: Record<string, unknown>): void {
    if (!this.isEnabled) return;

    // Queue warning like errors, respecting batching
    this.errorQueue.push({
      message: `[WARN] ${message}`,
      context: this.buildContext(metadata),
    });

    this.maybeFlush();
  }

  /**
   * Track a performance metric
   */
  trackPerformance(metric: Omit<PerformanceMetric, 'timestamp'>): void {
    if (!this.isEnabled) return;

    this.perfQueue.push({
      ...metric,
      timestamp: Date.now(),
    });

    // Send performance metrics immediately if queue gets large
    if (this.perfQueue.length >= 10) {
      this.flushPerformance();
    }
  }

  /**
   * Time a function execution
   */
  async timeAsync<T>(name: string, fn: () => Promise<T>, metadata?: Record<string, unknown>): Promise<T> {
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const duration = performance.now() - start;
      this.trackPerformance({
        name,
        value: duration,
        unit: 'ms',
        metadata,
      });
    }
  }

  /**
   * Time a synchronous function
   */
  timeSync<T>(name: string, fn: () => T, metadata?: Record<string, unknown>): T {
    const start = performance.now();
    try {
      return fn();
    } finally {
      const duration = performance.now() - start;
      this.trackPerformance({
        name,
        value: duration,
        unit: 'ms',
        metadata,
      });
    }
  }

  /**
   * Track WebSocket connection metrics
   */
  trackWsEvent(event: 'connect' | 'disconnect' | 'reconnect' | 'error', durationMs?: number): void {
    this.trackPerformance({
      name: `ws_${event}`,
      value: durationMs ?? 1,
      unit: 'count',
      metadata: {
        guildId: this.currentGuildId,
        userId: this.currentUserId,
      },
    });
  }

  /**
   * Track API call metrics
   */
  trackApiCall(endpoint: string, method: string, durationMs: number, success: boolean, statusCode?: number): void {
    this.trackPerformance({
      name: `api_${method.toLowerCase()}_${endpoint.replace(/\//g, '_')}`,
      value: durationMs,
      unit: 'ms',
      metadata: {
        success,
        statusCode,
        guildId: this.currentGuildId,
      },
    });
  }

  /**
   * Track user flow milestones
   */
  trackMilestone(milestone: 'page_load' | 'auth_complete' | 'garden_view' | 'first_interaction' | 'ws_connected', metadata?: Record<string, unknown>): void {
    this.trackPerformance({
      name: `milestone_${milestone}`,
      value: performance.now(),
      unit: 'ms',
      metadata: {
        ...metadata,
        guildId: this.currentGuildId,
        userId: this.currentUserId,
        url: window.location.href,
      },
    });
  }

  private setupErrorHandlers(): void {
    // GARDEN-210: Store bound handlers for cleanup
    this.boundErrorHandler = (event: ErrorEvent) => {
      this.captureError(event.error || new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    this.boundRejectionHandler = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
      this.captureError(error, { type: 'unhandledrejection' });
    };

    // Global error handler
    window.addEventListener('error', this.boundErrorHandler);

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', this.boundRejectionHandler);

    // Console error override to capture React/Vue errors
    this.originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      this.originalConsoleError!.apply(console, args);

      // Filter out React's warning about keys - too noisy
      const firstArg = args[0];
      if (typeof firstArg === 'string' && firstArg.includes('Each child in a list should have a unique "key"')) {
        return;
      }

      // Don't capture network errors - we track those separately
      if (typeof firstArg === 'string' && firstArg.includes('Failed to fetch')) {
        return;
      }

      const message = args.map(arg =>
        arg instanceof Error ? arg.message : String(arg)
      ).join(' ');

      if (message.length > 0) {
        this.captureWarning(`Console error: ${message.substring(0, 500)}`);
      }
    };
  }

  private setupPerformanceObserver(): void {
    // Core Web Vitals and performance metrics
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            this.trackPerformance({
              name: 'web_vital_lcp',
              value: lastEntry.startTime,
              unit: 'ms',
            });
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] as any });
        this.perfObservers.push(lcpObserver);

        // First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const fidEntry = entry as PerformanceEventTiming;
            this.trackPerformance({
              name: 'web_vital_fid',
              value: fidEntry.processingStart - fidEntry.startTime,
              unit: 'ms',
            });
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] as any });
        this.perfObservers.push(fidObserver);

        // Cumulative Layout Shift
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as any;
            if (!layoutShift.hadRecentInput) {
              clsValue += layoutShift.value;
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] as any });
        this.perfObservers.push(clsObserver);

        // GARDEN-210: Store bound handlers for cleanup
        this.boundVisibilityHandler = () => {
          if (document.visibilityState === 'hidden' && clsValue > 0) {
            this.trackPerformance({
              name: 'web_vital_cls',
              value: clsValue,
              unit: 'count',
            });
          }
        };

        this.boundLoadHandler = () => {
          setTimeout(() => {
            const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            if (navEntry) {
              this.trackPerformance({
                name: 'navigation_ttfb',
                value: navEntry.responseStart - navEntry.startTime,
                unit: 'ms',
              });
              this.trackPerformance({
                name: 'navigation_dom_complete',
                value: navEntry.domComplete - navEntry.startTime,
                unit: 'ms',
              });
              this.trackPerformance({
                name: 'navigation_load_complete',
                value: navEntry.loadEventEnd - navEntry.startTime,
                unit: 'ms',
              });
            }
          }, 0);
        };

        // Report CLS on page hide
        document.addEventListener('visibilitychange', this.boundVisibilityHandler);

        // Navigation timing
        window.addEventListener('load', this.boundLoadHandler);

      } catch (e) {
        // PerformanceObserver not supported
        this.captureWarning('PerformanceObserver not fully supported');
      }
    }
  }

  private buildContext(extraMetadata?: Record<string, unknown>): ErrorContext {
    return {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      userId: this.currentUserId || undefined,
      guildId: this.currentGuildId || undefined,
      metadata: extraMetadata,
    };
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);

    // GARDEN-210: Store bound handler for cleanup
    this.boundBeforeUnloadHandler = () => {
      this.flush();
    };

    // Flush on page unload
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);
  }

  private maybeFlush(): void {
    if (this.errorQueue.length >= this.MAX_QUEUE_SIZE) {
      this.flush();
    }
  }

  private flush(): void {
    this.flushErrors();
    this.flushPerformance();
  }

  private async flushErrors(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const batch = this.errorQueue.splice(0, this.MAX_QUEUE_SIZE);

    try {
      await this.sendBatchToServer(batch);
    } catch (e) {
      // Don't capture - avoid infinite loop
      console.error('[ErrorTracker] Failed to send errors batch:', e);
    }
  }

  private async flushPerformance(): Promise<void> {
    if (this.perfQueue.length === 0) return;

    const batch = this.perfQueue.splice(0, 20);

    try {
      await fetch(`${API_BASE_URL}/api/metrics/client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metrics: batch }),
        keepalive: true,
      });
    } catch (e) {
      // Silently fail - metrics are best-effort
      console.debug('[ErrorTracker] Failed to send metrics:', e);
    }
  }

  private async sendBatchToServer(batch: Array<{ message: string; stack?: string; context: ErrorContext }>): Promise<void> {
    // GARDEN-224: Actually batch errors into a single request for better performance
    // Transform batch into array of error payloads
    const errors = batch.map(item => ({
      message: item.message,
      stack: item.stack,
      url: item.context.url,
      userAgent: item.context.userAgent,
      timestamp: item.context.timestamp,
      userId: item.context.userId,
      guildId: item.context.guildId,
      metadata: item.context.metadata,
    }));

    // Send as a single batched request to reduce network overhead
    const response = await fetch(`${API_BASE_URL}/api/log-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors }),
      keepalive: true,
    });

    // If server doesn't support batch format, fall back to individual requests
    if (response.status === 422 || response.status === 400) {
      const promises = errors.map(payload =>
        fetch(`${API_BASE_URL}/api/log-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        })
      );
      await Promise.all(promises);
    }
  }

  /**
   * GARDEN-210: Dispose of the error tracker and clean up all resources.
   * Call this when shutting down the app to prevent memory leaks.
   */
  dispose(): void {
    // Stop the flush timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Disconnect all performance observers
    this.perfObservers.forEach(observer => {
      try {
        observer.disconnect();
      } catch {
        // Ignore errors during cleanup
      }
    });
    this.perfObservers = [];

    // Remove event listeners
    if (this.boundErrorHandler) {
      window.removeEventListener('error', this.boundErrorHandler);
      this.boundErrorHandler = null;
    }
    if (this.boundRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.boundRejectionHandler);
      this.boundRejectionHandler = null;
    }
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
      this.boundBeforeUnloadHandler = null;
    }
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
    if (this.boundLoadHandler) {
      window.removeEventListener('load', this.boundLoadHandler);
      this.boundLoadHandler = null;
    }

    // Restore original console.error
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
      this.originalConsoleError = null;
    }

    // Flush any remaining data
    this.flush();

    // Clear the singleton instance so it can be recreated if needed
    ErrorTracker.instance = null as any;
  }
}

// Singleton instance
export const errorTracker = ErrorTracker.getInstance();

/**
 * React/Vue error boundary helper
 */
export function withErrorTracking<T extends (...args: any[]) => any>(
  fn: T,
  componentName?: string
): T {
  return ((...args: any[]) => {
    try {
      return fn(...args);
    } catch (error) {
      errorTracker.captureError(
        error instanceof Error ? error : new Error(String(error)),
        { component: componentName, args: args.map(a => typeof a) }
      );
      throw error;
    }
  }) as T;
}

/**
 * Async wrapper with error tracking
 */
export async function withAsyncErrorTracking<T>(
  fn: () => Promise<T>,
  operationName: string
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    errorTracker.captureError(
      error instanceof Error ? error : new Error(String(error)),
      { operation: operationName }
    );
    throw error;
  }
}

/**
 * Initialize error tracking with context
 */
export function initErrorTracking(guildId?: string, userId?: string): void {
  errorTracker.setContext(guildId, userId);
  errorTracker.trackMilestone('page_load');
}
